import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { Store } from '../../stateManagement';
import { IFrequency, ConnectionPoint, TagDefinition as TagDefinition, IValueViewModel, ConnectionPointType, IDateRange, ActualDataType, AcquisitionStrategy } from '../../models/index';
import { IDateRangeService } from '../../services/';
import { TagManualInputValues } from '../../models/tagManualInputValues';

import { toTagManualInputValues } from '../../widgets/manualInputs/manualInputs.helper';
import { toDictionary } from '../../common';

export interface ManualInputStateViewModels {
    isLoading: boolean;
    frequencies: IFrequency[];
    viewModels: TagManualInputValues[];
    isMobileMode: boolean;
}

export interface MapManualInputDispatchToThis {
    load: () => angular.IPromise<any[]>;
}

export interface IManualInputsStateService {
    getInitialDates(dates: any): IDateRange;
    prepareViewModels(store: Store, defaultCpGuid: string): ManualInputStateViewModels;
}

export class ManualInputsStateService implements IManualInputsStateService {
    static serviceId = 'manualInputsStateService';

    static $inject = ['dateRangeService'];

    constructor(private _dateRangeService: IDateRangeService) {
    }

    getInitialDates(dates: any): IDateRange {
        let initialRange: IDateRange;
        if (dates.beginDate && dates.endDate) {
            initialRange = {
                beginDate: moment(dates.beginDate),
                endDate: moment(dates.endDate)
            };
        } else if (this._dateRangeService.initialized()) {
            initialRange = {
                beginDate: moment(this._dateRangeService.beginDate() as string),
                endDate: moment(this._dateRangeService.endDate() as string)
            };
        } else {
            const endMoment = moment().startOf('day');
            const beginMoment = endMoment.clone().subtract(7, 'd');
            initialRange = {
                beginDate: beginMoment,
                endDate: endMoment
            };
        }

        return initialRange;
    }

    prepareViewModels(store: Store, defaultCpGuid: string): ManualInputStateViewModels {
        const { connectionPoints, tags, digitalSets } = store;
        const valueViewModels = store.valueViewModels.dictionary;
        const manualCp = toDictionary(_.values(connectionPoints.dictionary).filter(cp => cp.type === ConnectionPointType.Manual));
        const defaultCp = connectionPoints.dictionary[defaultCpGuid];
        const defaultFreq = {
            interval: defaultCp?.interval,
            intervalTimeUnit: defaultCp?.intervalTimeUnit
        };

        let viewModels: TagManualInputValues[] = [];
        let freqs: IFrequency[] = [];

        if (Object.keys(manualCp).length) {
            const manualTags = _.values<TagDefinition>(tags.dictionary)
                .filter(t => t.acquisitionStrategy === AcquisitionStrategy.Default && manualCp[t.acquisitionPointGuid]);

            if (manualTags.length) {
                viewModels = manualTags.map(t => {
                    const valueViewModel = valueViewModels[t.guid];
                    let retval: TagManualInputValues;
                    if (valueViewModel) {
                        retval = toTagManualInputValues(valueViewModel, defaultFreq);
                    } else {
                        retval = {} as any;
                        retval.guid = t.guid;
                        retval.name = t.name;
                        retval.frequency = defaultFreq;
                    }
                    retval.dataType = t.actualDataType;
                    if (t.actualDataType === ActualDataType.Digital) {
                        //local copy that will get mutated
                        retval.digitalSet = angular.copy(digitalSets.dictionary[t.digitalSetGuid]);
                        if (retval.digitalSet) {
                            retval.digitalSet.stateCollection.unshift({ code: null, name: '' });
                        }
                    }
                    return retval;
                });
                freqs = this.getFrequencies(viewModels);
            }
        }
        return {
            viewModels,
            frequencies: freqs,
            isLoading: false,
            isMobileMode: store.layout.value.isMobileMode
        };
    }

    private getFrequencies(viewModels: TagManualInputValues[]): IFrequency[] {
        let freqs: IFrequency[] = [];
        viewModels
            .map(vm => vm.frequency)
            .forEach(currentValue => {
                if (!_.some<IFrequency>(freqs, f => f.interval === currentValue.interval && f.intervalTimeUnit === currentValue.intervalTimeUnit)) {
                    freqs.push(currentValue);
                }
            });
        freqs = freqs.sort((a, b) => {
            return a.intervalTimeUnit > b.intervalTimeUnit
                ? 1
                : a.intervalTimeUnit < b.intervalTimeUnit
                    ? -1
                    : a.interval > b.interval
                        ? 1
                        : -1;
        });
        return freqs;
    }
}